/**
 * Epic-Ready PDF Summary Generator (HIM / Audit)
 * 
 * Generates a comprehensive, printable PDF including:
 * - Operative note
 * - CPT reconciliation table
 * - Proof map summary
 * - Surgeon confirmations
 * - Authorization scope
 * - Full audit timeline
 * 
 * Labeled: "Prepared by LINK — Documentation & Revenue Integrity Support"
 */

import { jsPDF } from 'jspdf';
import type { ExportCptLine, ExportAuditEvent } from './exportPackageGenerator';

export interface EpicPdfInput {
  /** Case metadata */
  caseNumber: string;
  authReference?: string;
  dateOfService?: string;
  laterality?: string;
  surgeonName?: string;
  assistantSurgeonName?: string;
  facilityName?: string;
  
  /** Attestation */
  attestationText?: string;
  attestationTimestamp?: string;
  
  /** Operative note text (for inclusion) */
  operativeNoteText?: string;
  
  /** CPT lines */
  cptLines: ExportCptLine[];
  
  /** Authorization scope info */
  authorizedCpts: string[];
  authorizationScope?: string;
  
  /** Proof map summary */
  proofMapSummary?: ProofMapSummaryItem[];
  
  /** Surgeon confirmations */
  surgeonConfirmations?: SurgeonConfirmationItem[];
  
  /** Audit timeline */
  auditTimeline: ExportAuditEvent[];
  
  /** Demo mode */
  isDemoExport: boolean;
  generatedAt: string;
}

export interface ProofMapSummaryItem {
  cptCode: string;
  documentationStatus: string;
  requirementsSatisfied: number;
  requirementsTotal: number;
  evidenceSnippetCount: number;
}

export interface SurgeonConfirmationItem {
  cptCode: string;
  confirmation: string;
  billableStatus: string;
  confirmedAt?: string;
}

export function generateEpicPdf(input: EpicPdfInput): jsPDF {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // Helpers
  const checkPage = (needed: number = 12) => {
    if (y > doc.internal.pageSize.getHeight() - needed - 15) {
      doc.addPage();
      y = margin;
    }
  };

  const addText = (text: string, fontSize = 9, bold = false) => {
    checkPage(fontSize * 0.6);
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(text, contentWidth);
    doc.text(lines, margin, y);
    y += lines.length * (fontSize * 0.45) + 2;
  };

  const addSectionHeader = (title: string) => {
    checkPage(14);
    y += 3;
    doc.setFillColor(15, 23, 42);
    doc.rect(margin, y - 5, contentWidth, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin + 3, y);
    doc.setTextColor(0, 0, 0);
    y += 8;
  };

  const addKeyValue = (key: string, value: string) => {
    checkPage();
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${key}:`, margin, y);
    doc.setFont('helvetica', 'normal');
    const valLines = doc.splitTextToSize(value, contentWidth - 55);
    doc.text(valLines, margin + 55, y);
    y += valLines.length * 4 + 2;
  };

  // ─── HEADER ──────────────────────────────────────────────────────
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 35, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Revenue Integrity Export', margin, 14);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Case: ${input.caseNumber}`, margin, 22);
  if (input.authReference) doc.text(`Auth: ${input.authReference}`, margin, 28);
  doc.text(`Generated: ${new Date(input.generatedAt).toLocaleString()}`, pageWidth - margin - 65, 22);

  if (input.isDemoExport) {
    doc.setFillColor(245, 158, 11);
    doc.rect(pageWidth - margin - 55, 25, 55, 7, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('DEMO — ILLUSTRATIVE ONLY', pageWidth - margin - 53, 30);
  }

  doc.setTextColor(0, 0, 0);
  y = 42;

  // ─── CASE INFORMATION ────────────────────────────────────────────
  addSectionHeader('CASE INFORMATION');
  addKeyValue('Case Number', input.caseNumber);
  if (input.dateOfService) addKeyValue('Date of Service', input.dateOfService);
  if (input.laterality) addKeyValue('Laterality', input.laterality);
  if (input.surgeonName) addKeyValue('Surgeon', input.surgeonName);
  if (input.assistantSurgeonName) addKeyValue('Assistant Surgeon', input.assistantSurgeonName);
  if (input.facilityName) addKeyValue('Facility', input.facilityName);

  // ─── SURGEON ATTESTATION ─────────────────────────────────────────
  if (input.attestationText) {
    addSectionHeader('SURGEON ATTESTATION');
    addText(input.attestationText, 9);
    if (input.attestationTimestamp) {
      addText(`Attested: ${new Date(input.attestationTimestamp).toLocaleString()}`, 8);
    }
  }

  // ─── OPERATIVE NOTE ──────────────────────────────────────────────
  if (input.operativeNoteText) {
    addSectionHeader('OPERATIVE NOTE');
    // Print the note with word wrapping
    const noteLines = input.operativeNoteText.split('\n');
    for (const line of noteLines.slice(0, 80)) { // Cap at 80 lines for PDF
      addText(line || ' ', 8);
    }
    if (noteLines.length > 80) {
      addText(`... (${noteLines.length - 80} additional lines)`, 8);
    }
  }

  // ─── CPT RECONCILIATION TABLE ────────────────────────────────────
  addSectionHeader('CPT RECONCILIATION TABLE');
  y += 2;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Seq', margin, y);
  doc.text('Code', margin + 12, y);
  doc.text('Modifiers', margin + 40, y);
  doc.text('Units', margin + 75, y);
  doc.text('Description', margin + 90, y);
  y += 4;
  doc.setDrawColor(150);
  doc.line(margin, y - 1, pageWidth - margin, y - 1);
  y += 2;

  doc.setFont('helvetica', 'normal');
  for (const cpt of input.cptLines) {
    checkPage();
    doc.text(String(cpt.sequence), margin, y);
    doc.text(cpt.finalCodeString, margin + 12, y);
    doc.text(cpt.modifiers.join(', ') || '—', margin + 40, y);
    doc.text(String(cpt.units), margin + 75, y);
    doc.text((cpt.cptDescription || '').substring(0, 35), margin + 90, y);
    y += 5;
  }

  // Modifier rationale
  const hasExplanations = input.cptLines.some(l => Object.keys(l.modifierExplanations).length > 0);
  if (hasExplanations) {
    y += 3;
    addText('Modifier Rationale:', 9, true);
    for (const cpt of input.cptLines) {
      for (const [mod, reason] of Object.entries(cpt.modifierExplanations)) {
        addText(`  ${cpt.cptCode}-${mod}: ${reason}`, 8);
      }
    }
  }

  // ─── PROOF MAP SUMMARY ───────────────────────────────────────────
  if (input.proofMapSummary && input.proofMapSummary.length > 0) {
    addSectionHeader('DOCUMENTATION PROOF MAP');
    for (const item of input.proofMapSummary) {
      checkPage();
      const status = item.documentationStatus === 'sufficient' ? '✓' : 
                     item.documentationStatus === 'ambiguous' ? '?' : '✗';
      addText(`${status} CPT ${item.cptCode}: ${item.requirementsSatisfied}/${item.requirementsTotal} requirements satisfied (${item.evidenceSnippetCount} evidence snippets)`, 8);
    }
  }

  // ─── SURGEON CONFIRMATIONS ───────────────────────────────────────
  if (input.surgeonConfirmations && input.surgeonConfirmations.length > 0) {
    addSectionHeader('SURGEON CONFIRMATIONS');
    for (const conf of input.surgeonConfirmations) {
      checkPage();
      const label = conf.confirmation === 'confirmed_performed' ? '✓ Confirmed' : 
                    conf.confirmation === 'not_performed' ? '✗ Not Performed' : '? ' + conf.confirmation;
      addText(`${label} — CPT ${conf.cptCode} → Billable: ${conf.billableStatus}${conf.confirmedAt ? ' @ ' + new Date(conf.confirmedAt).toLocaleString() : ''}`, 8);
    }
  }

  // ─── AUTHORIZATION SCOPE ─────────────────────────────────────────
  addSectionHeader('AUTHORIZATION SCOPE');
  addKeyValue('Scope', input.authorizationScope || 'Standard');
  addKeyValue('Authorized CPTs', input.authorizedCpts.join(', '));
  addKeyValue('Exported CPTs', input.cptLines.map(l => l.cptCode).join(', '));

  // ─── AUDIT TIMELINE ──────────────────────────────────────────────
  if (input.auditTimeline.length > 0) {
    addSectionHeader('AUDIT TIMELINE');
    for (const event of input.auditTimeline.slice(0, 40)) {
      checkPage();
      const ts = new Date(event.timestamp).toLocaleString();
      addText(`[${ts}] ${event.action}${event.actor ? ` (${event.actor})` : ''}${event.details ? ': ' + event.details : ''}`, 7);
    }
    if (input.auditTimeline.length > 40) {
      addText(`... and ${input.auditTimeline.length - 40} more events`, 7);
    }
  }

  // ─── FOOTER ──────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(241, 245, 249);
    doc.rect(0, doc.internal.pageSize.getHeight() - 14, pageWidth, 14, 'F');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(
      'Documentation & Revenue Integrity Support',
      margin,
      doc.internal.pageSize.getHeight() - 6,
    );
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - margin - 20,
      doc.internal.pageSize.getHeight() - 6,
    );
    if (input.isDemoExport) {
      doc.setTextColor(217, 119, 6);
      doc.setFont('helvetica', 'bold');
      doc.text('DEMO — NOT FOR BILLING USE', pageWidth / 2 - 20, doc.internal.pageSize.getHeight() - 6);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
    }
  }

  return doc;
}
